import React, { useEffect, useState, useRef } from "react"
import { InlineWidget, CalendlyEventData } from "react-calendly"
import {
  useCreateOrganizationUserMutation,
  useGetOnboardingCompletionQuery,
  useUpdateOnboardingAppointmentMutation,
} from "../app/services/meseeknaApiV2"
import HeaderSection from "../components/header-section"
import OnboardingSteps from "../components/OnboardingSteps"
import Sidebar from "../features/sidebar/components/sidebar"
import { HiChevronDown } from "react-icons/hi"
import { useTypedSelector } from "../app/store"
import { fixIcon } from "../utils/test-utils"
import { useNavigate } from "react-router-dom"
import { trackOnboardingStep } from "../lib/analytics"
const HiChevronDownIcon = fixIcon(HiChevronDown)
const ROLES_CHOICES = [
  {
    id: 4,
    code: 400,
    label: "Team Member",
    priceLabel: "first 10 included, $20/month additional",
  },
  {
    id: 3,
    code: 300,
    label: "Recruiter",
    priceLabel: "unlimited users, $80/month",
  },
]

type UserEntry = {
  id: string
  firstName: string
  lastName: string
  email: string
  role: number
  status: "pending" | "success" | "error"
  error?: string
}

const OnboardingSetup: React.FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    trackOnboardingStep("setup")
  }, [])

  // Separate state for created users and current input form
  const [createdUsers, setCreatedUsers] = useState<UserEntry[]>([])
  const [currentUser, setCurrentUser] = useState<UserEntry>({
    id: "1",
    firstName: "",
    lastName: "",
    email: "",
    role: 4, // Default to Team Member
    status: "pending",
  })
  const [isCreating, setIsCreating] = useState(false)
  const [createUser] = useCreateOrganizationUserMutation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const { data: onboardingData, refetch: refetchOnboardingData } =
    useGetOnboardingCompletionQuery(Number(organization?.id) || 0, {
      skip: !organization?.id,
    })
  const [updateOnboardingAppointment] = useUpdateOnboardingAppointmentMutation()

  // Field validation state
  const [validationErrors, setValidationErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
  })

  // Track which fields have been touched
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
  })
  const [appointmentScheduledLocally, setAppointmentScheduledLocally] =
    useState(false)
  const [attemptedFinish, setAttemptedFinish] = useState(false)
  const requiresCalendly = onboardingData?.data?.selectedScenario === "custom"
  const remoteAppointmentScheduled =
    onboardingData?.data?.onboardingAppointmentScheduled ?? false
  const hasScheduledAppointment =
    remoteAppointmentScheduled || appointmentScheduledLocally
  const canCompleteOnboarding = !requiresCalendly || hasScheduledAppointment

  const resetForm = () => {
    setCurrentUser({
      id: Date.now().toString(),
      firstName: "",
      lastName: "",
      email: "",
      role: 4,
      status: "pending",
    })

    // Reset validation state too
    setValidationErrors({
      firstName: "",
      lastName: "",
      email: "",
    })

    setTouchedFields({
      firstName: false,
      lastName: false,
      email: false,
    })
  }

  // More robust email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email.trim())
  }

  // Name validation (prevent numbers, special chars, etc.)
  const isValidName = (name: string) => {
    if (!name.trim()) return false
    // Allow letters, spaces, hyphens, and apostrophes
    return /^[a-zA-Z\s'-]+$/.test(name.trim())
  }

  // Validate a field and return error message
  const validateField = (
    field: "firstName" | "lastName" | "email",
    value: string,
  ): string => {
    switch (field) {
      case "firstName":
        if (!value.trim()) return "First name is required"
        if (!isValidName(value)) return "Please enter a valid first name"
        if (value.trim().length < 2)
          return "First name must be at least 2 characters"
        return ""

      case "lastName":
        if (!value.trim()) return "Last name is required"
        if (!isValidName(value)) return "Please enter a valid last name"
        if (value.trim().length < 2)
          return "Last name must be at least 2 characters"
        return ""

      case "email":
        if (!value.trim()) return "Email address is required"
        if (!isValidEmail(value)) return "Please enter a valid email address"
        return ""

      default:
        return ""
    }
  }

  // Update user data and validate in real-time
  const updateCurrentUser = (
    field: "firstName" | "lastName" | "email" | "role",
    value: string | number,
  ) => {
    setCurrentUser({
      ...currentUser,
      [field]: value,
      status: "pending",
    })

    // Mark field as touched
    if (field !== "role" && !touchedFields[field]) {
      setTouchedFields({
        ...touchedFields,
        [field]: true,
      })
    }

    // Validate text fields
    if (field !== "role") {
      const errorMessage = validateField(field, value as string)
      setValidationErrors({
        ...validationErrors,
        [field]: errorMessage,
      })
    }
  }

  // Handle field blur for validation
  const handleBlur = (field: "firstName" | "lastName" | "email") => {
    setTouchedFields({
      ...touchedFields,
      [field]: true,
    })

    const errorMessage = validateField(field, currentUser[field] as string)
    setValidationErrors({
      ...validationErrors,
      [field]: errorMessage,
    })
  }

  // All fields have valid data
  const canSubmit =
    currentUser.firstName.trim() &&
    currentUser.lastName.trim() &&
    isValidEmail(currentUser.email.trim()) &&
    !validationErrors.firstName &&
    !validationErrors.lastName &&
    !validationErrors.email

  // Get role label by id
  const getRoleLabel = (roleId: number) => {
    const role = ROLES_CHOICES.find((r) => r.id === roleId)
    return role ? role.label : "Unknown Role"
  }

  const getPriceLabel = (roleId: number) => {
    const role = ROLES_CHOICES.find((r) => r.id === roleId)
    return role ? role.priceLabel : "Unknown Role"
  }

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleCreateUser = (roleId: number) => {
    // Mark all fields as touched
    setTouchedFields({
      firstName: true,
      lastName: true,
      email: true,
    })

    // Validate all fields
    const firstNameError = validateField("firstName", currentUser.firstName)
    const lastNameError = validateField("lastName", currentUser.lastName)
    const emailError = validateField("email", currentUser.email)

    setValidationErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
    })

    // Check if we can submit
    if (!canSubmit || firstNameError || lastNameError || emailError) return

    // Create user directly with the selected role ID
    setIsCreating(true)
    setIsDropdownOpen(false)

    createUser({
      firstName: currentUser.firstName.trim(),
      lastName: currentUser.lastName.trim(),
      email: currentUser.email.trim(),
      role: roleId, // Use the role ID directly from the parameter
    })
      .unwrap()
      .then(() => {
        const successUser: UserEntry = {
          ...currentUser,
          firstName: currentUser.firstName.trim(),
          lastName: currentUser.lastName.trim(),
          email: currentUser.email.trim(),
          role: roleId, // Use the selected role ID
          status: "success" as const,
        }

        // Add to created users list
        setCreatedUsers([...createdUsers, successUser])

        // Reset the form
        resetForm()
      })
      .catch((error: unknown) => {
        const err = error as { data?: { message?: string } }
        setCurrentUser({
          ...currentUser,
          status: "error" as const,
          error: err.data?.message || "Failed to create user",
        })
      })
      .finally(() => {
        setIsCreating(false)
      })
  }

  const handleAppointmentScheduled = async (e: { data: CalendlyEventData }) => {
    console.log("Appointment scheduled:", e.data)

    try {
      if (!organization?.id) {
        console.error("No organization ID available for appointment update")
        return
      }

      await updateOnboardingAppointment({
        organizationId: organization.id,
        scheduled: true,
      }).unwrap()

      setAppointmentScheduledLocally(true)
      await refetchOnboardingData()
    } catch (error) {
      console.error("Error updating onboarding appointment status:", error)
    }
  }

  const handleFinish = () => {
    setAttemptedFinish(true)
    if (!canCompleteOnboarding) {
      return
    }

    navigate(`/onboarding/success?count=${createdUsers.length}`)
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200">
          <OnboardingSteps currentStep={3} />

          {/* Back button */}
          <div className="mb-4">
            <button
              onClick={() => navigate("/onboarding/measures?step=2")}
              className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
              ← Back to Scenarios
            </button>
          </div>

          <div className="w-full h-full flex-1 flex flex-col items-start justify-start py-0 px-0 box-border gap-[12px] text-base text-gray-700 mt-4">
            <div className="w-full h-full flex gap-8">
              <div className="flex-1 flex flex-col h-full">
                <h2 className="text-2xl font-semibold mb-4">
                  Schedule Your Onboarding Call
                </h2>
                <p className="text-gray-600 mb-6 max-w-xl leading-relaxed">
                  Get the most from your cognitive simulations with a quick
                  onboarding call – we&apos;ll answer questions, explain the
                  process, and ensure you&apos;re set up for predictive
                  insights.
                </p>
                <div className="bg-white rounded-lg shadow-md p-6 flex-1 mb-6 min-h-0">
                  <InlineWidget
                    url={process.env.REACT_APP_CALENDLY_URL!}
                    styles={{ height: "100%", minHeight: "500px" }}
                    pageSettings={{
                      hideEventTypeDetails: true,
                      hideLandingPageDetails: true,
                    }}
                    onEventScheduled={handleAppointmentScheduled}
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col pb-6">
                <h2 className="text-2xl font-semibold mb-4">Add Your Team</h2>
                <p className="text-gray-600 mb-4 max-w-xl leading-relaxed">
                  Enter your team members who will complete the simulation.
                  They&apos;ll receive invitations to begin their cognitive assessment.
                </p>

                {/* Recruiter callout box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 max-w-xl">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2 text-lg">🎯</span>
                    <div>
                      <p className="text-blue-800 font-medium text-sm">
                        Screening job candidates?
                      </p>
                      <p className="text-blue-700 text-sm mt-1">
                        Add your recruiting team members and they can deploy
                        simulations to up to 100 potential hires for cognitive
                        fit screening—7X more predictive than traditional methods.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Participant counter */}
                {createdUsers.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 font-medium">
                      {createdUsers.length} participants added
                    </p>
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <div className="space-y-4">
                    <div className="rounded-lg bg-white">
                      <div className="flex flex-col gap-4">
                        <div className="flex gap-4">
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={currentUser.firstName}
                              onChange={(e) =>
                                updateCurrentUser("firstName", e.target.value)
                              }
                              onBlur={() => handleBlur("firstName")}
                              className={`p-2 rounded-md text-sm box-border border ${
                                touchedFields.firstName &&
                                validationErrors.firstName
                                  ? "border-red-400 bg-red-50"
                                  : touchedFields.firstName &&
                                      !validationErrors.firstName
                                    ? "border-green-400 bg-green-50"
                                    : "border-gray-300"
                              } focus:ring-primary-500 focus:border-primary-500 w-full`}
                              placeholder="First Name"
                            />
                            {touchedFields.firstName &&
                              validationErrors.firstName && (
                                <div className="mt-1 text-xs text-red-600">
                                  {validationErrors.firstName}
                                </div>
                              )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={currentUser.lastName}
                              onChange={(e) =>
                                updateCurrentUser("lastName", e.target.value)
                              }
                              onBlur={() => handleBlur("lastName")}
                              className={`p-2 rounded-md text-sm box-border border ${
                                touchedFields.lastName &&
                                validationErrors.lastName
                                  ? "border-red-400 bg-red-50"
                                  : touchedFields.lastName &&
                                      !validationErrors.lastName
                                    ? "border-green-400 bg-green-50"
                                    : "border-gray-300"
                              } focus:ring-primary-500 focus:border-primary-500 w-full`}
                              placeholder="Last Name"
                            />
                            {touchedFields.lastName &&
                              validationErrors.lastName && (
                                <div className="mt-1 text-xs text-red-600">
                                  {validationErrors.lastName}
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="w-full">
                          <input
                            type="email"
                            value={currentUser.email}
                            onChange={(e) =>
                              updateCurrentUser("email", e.target.value)
                            }
                            onBlur={() => handleBlur("email")}
                            className={`p-2 rounded-md text-sm box-border border ${
                              touchedFields.email && validationErrors.email
                                ? "border-red-400 bg-red-50"
                                : touchedFields.email && !validationErrors.email
                                  ? "border-green-400 bg-green-50"
                                  : currentUser.status === "error"
                                    ? "border-red-400"
                                    : "border-gray-300"
                            } focus:ring-primary-500 focus:border-primary-500 w-full`}
                            placeholder="Email Address"
                          />
                          {touchedFields.email && validationErrors.email && (
                            <div className="mt-1 text-xs text-red-600">
                              {validationErrors.email}
                            </div>
                          )}
                          {!validationErrors.email && (
                            <div className="mt-1 text-xs text-gray-500">
                              They&apos;ll receive an invitation immediately
                            </div>
                          )}
                        </div>

                        {currentUser.status === "error" && (
                          <div className="text-sm text-red-600 font-medium">
                            {currentUser.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-4">
                    <div className="relative inline-block" ref={dropdownRef}>
                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        disabled={isCreating || !canSubmit}
                        title={
                          !canSubmit &&
                          (touchedFields.firstName ||
                            touchedFields.lastName ||
                            touchedFields.email)
                            ? "Please fill all fields correctly"
                            : ""
                        }
                        className={`min-w-[180px] rounded-md text-white cursor-pointer inline-flex items-stretch ${
                          isCreating
                            ? "bg-blue-400 cursor-wait"
                            : !canSubmit
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-primary-600 hover:bg-primary-700"
                        }`}>
                        {isCreating ? (
                          <div className="flex-1 flex items-center justify-center gap-2 py-2.5 px-6">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Creating...
                          </div>
                        ) : (
                          <>
                            <span className="flex-1 font-medium py-2.5 px-4 text-center">
                              {!canSubmit &&
                              (touchedFields.firstName ||
                                touchedFields.lastName ||
                                touchedFields.email)
                                ? "Add Another Person"
                                : "Create User"}
                            </span>
                            <span className={`w-10 flex items-center justify-center border-l border-white/20 ${isDropdownOpen ? "" : ""}`}>
                              <HiChevronDownIcon
                                className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                              />
                            </span>
                          </>
                        )}
                      </button>

                      {isDropdownOpen && !isCreating && canSubmit && (
                        <div className="absolute right-0 mt-1 w-[280px] bg-gray-50 rounded-lg shadow-lg z-10 border border-gray-200 overflow-hidden">
                          {ROLES_CHOICES.map((role) => (
                            <button
                              key={role.id}
                              onClick={() => handleCreateUser(role.id)}
                              className="cursor-pointer w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-100 border-b border-gray-200 last:border-b-0">
                              <div className="font-medium text-[15px]">
                                {role.label}
                              </div>
                              <div className="text-sm text-gray-500 mt-0.5">
                                {getPriceLabel(role.id)}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleFinish}
                      disabled={!canCompleteOnboarding}
                      className={`px-6 py-2.5 rounded-md font-medium inline-flex items-center justify-center transition-colors ${
                        canCompleteOnboarding
                          ? "text-white bg-blue-500 hover:bg-blue-600 cursor-pointer"
                          : "text-gray-500 bg-gray-200 cursor-not-allowed"
                      }`}>
                      <span>Send All Invitations</span>
                    </button>
                    {requiresCalendly && !hasScheduledAppointment && (
                      <p
                        className={`mt-2 text-sm ${
                          attemptedFinish ? "text-red-600" : "text-orange-600"
                        }`}>
                        Because you selected a custom scenario, please book your
                        onboarding call before continuing.
                      </p>
                    )}
                  </div>
                </div>

                {/* Created Users Table - Moved below the user creation widget */}
                {createdUsers.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <h3 className="text-lg font-semibold mb-3">
                      Created Users
                    </h3>
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {createdUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {user.email}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {getRoleLabel(user.role)}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Active
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingSetup
